'use client';

import type { ReactNode } from 'react';

import { TOP_NAV_DATA_GA } from '@/app/components/layouts/top-navigation/tracking';
import type { VehicleType } from '@/app/types/vehicle-type';
import { createDataGA } from '@/app/utils/createDataGA';
import { Button } from '@/components/shared/buttons/button';
import { ButtonIcon } from '@/components/shared/buttons/button-icon';
import { Link } from '@/components/shared/element/link';
import { Box } from '@/components/shared/layout/box';
import type { CSS } from '@/stitches.config';

interface MobileMenuNavProps {
  Logo?: ReactNode;
  vehicleType: VehicleType;
  css?: CSS;
  activeLabel?: string;
  onBack: () => void;
  onClose: () => void;
}

export const MobileMenuNav = ({
  Logo,
  vehicleType,
  css = {},
  activeLabel,
  onBack,
  onClose,
}: MobileMenuNavProps) => {
  return (
    <Box
      component="nav"
      display="flex"
      alignItems="center"
      justifyContent="between"
      backgroundColor="white"
      css={{
        borderBottom: '1px solid #ebebeb', // tailwind gray-50
        height: '$size-14', // Top Navigation Height
        px: '$space-4',
        '@md': {
          px: '$space-10',
        },
        ...css,
      }}
    >
      {activeLabel ? (
        <Button
          variant="button-red"
          icon="chevron-left"
          iconPosition="leading"
          isTransparent
          css={{
            '--icon-color': 'var(--colors-primary)',
            '--hover-icon-color': 'var(--colors-primary100)',
            columnGap: '$space-2',
          }}
          onClick={onBack}
          tracking={{
            dataGA: createDataGA({
              eventName: TOP_NAV_DATA_GA.topNavigation,
              text: 'Mobile menu back',
              click: true,
            }),
          }}
          label={activeLabel}
        />
      ) : (
        <Link
          href="/"
          tracking={{
            dataGA: createDataGA({
              eventName: TOP_NAV_DATA_GA.topNavigation,
              text: TOP_NAV_DATA_GA.logo,
              url: '/',
              click: true,
            }),
          }}
          css={{
            position: 'relative',
            aspectRatio: '8 / 3',
            display: 'flex',
            alignItems: 'center',
            color: '$primary',
            '& svg': {
              width: vehicleType === 'lcv' ? '$size-24' : 132,
            },
          }}
        >
          {Logo}
        </Link>
      )}
      <ButtonIcon
        variant="button-icon-color-gray-dark"
        isTransparent
        icon="close"
        onClick={onClose}
        tracking={{
          dataGA: createDataGA({
            eventName: TOP_NAV_DATA_GA.topNavigation,
            text: 'Mobile menu close',
            click: true,
          }),
        }}
      />
    </Box>
  );
};
